import { useAppSelector } from "~/hooks";
import { GeneratingSpinner } from "../components/GeneratingSpinner";
import { SectionErrorBoundary } from "../components/SectionErrorBoundary";
import { SectionWrapper } from "../components/SectionWrapper";
import { StaleWarning } from "../components/StaleWarning";
import type { SectionStatus } from "../hooks/usePageViewData";
import styles from "./CustomizeSpecSection.module.scss";

type CustomizeSpecSectionProps = {
  number: number;
  status: SectionStatus;
  detailPath: string;
  onGenerate: (documentType: string) => void;
  onSkip?: () => void;
  canRegenerate: boolean;
};

const MAX_ROWS = 10;

const TABLE_COLUMNS = ["機能名", "適用アプリ名", "概要", "対応区分"] as const;

export const CustomizeSpecSection = ({
  number,
  status,
  detailPath,
  onGenerate,
  onSkip,
  canRegenerate,
}: CustomizeSpecSectionProps) => {
  const { data } = useAppSelector((s) => s.customizeSpecificationList);

  const renderContent = () => {
    if (data.length === 0) {
      return <div className={styles["fallback"]}>カスタマイズ仕様データがありません</div>;
    }

    const visibleRows = data.slice(0, MAX_ROWS);
    const remainingCount = data.length - MAX_ROWS;

    return (
      <>
        <div className={styles["tableWrapper"]}>
          <table className={styles["table"]}>
            <thead>
              <tr>
                {TABLE_COLUMNS.map((col) => (
                  <th key={col} className={styles["th"]}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => (
                <tr
                  key={`${row["機能名"]}-${index}`}
                  className={index % 2 === 1 ? styles["trEven"] : styles["trOdd"]}
                >
                  {TABLE_COLUMNS.map((col) => (
                    <td key={col} className={styles["td"]}>
                      {row[col]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {remainingCount > 0 && (
          <p className={styles["moreMessage"]}>他 {remainingCount} 件は詳細ページで確認 →</p>
        )}
      </>
    );
  };

  if (status === "generating") {
    return (
      <SectionWrapper
        number={number}
        title="実装するカスタマイズ（カスタマイズ仕様書）"
        status={status}
        detailPath={detailPath}
        onGenerate={() => onGenerate("customize-specification")}
        disableGenerate={!canRegenerate}
      >
        <GeneratingSpinner />
      </SectionWrapper>
    );
  }

  if (status === "ready") {
    return (
      <SectionWrapper
        number={number}
        title="実装するカスタマイズ（カスタマイズ仕様書）"
        status={status}
        detailPath={detailPath}
        onGenerate={() => onGenerate("customize-specification")}
        disableGenerate={!canRegenerate}
      >
        {onSkip !== undefined && (
          <div className={styles["skipButtonWrapper"]}>
            <button type="button" className={styles["skipButton"]} onClick={onSkip}>
              スキップ
            </button>
          </div>
        )}
      </SectionWrapper>
    );
  }

  if (status === "stale") {
    return (
      <SectionWrapper
        number={number}
        title="実装するカスタマイズ（カスタマイズ仕様書）"
        status={status}
        detailPath={detailPath}
        onGenerate={() => onGenerate("customize-specification")}
        disableGenerate={!canRegenerate}
      >
        <SectionErrorBoundary
          sectionName="実装するカスタマイズ（カスタマイズ仕様書）"
          detailPath={detailPath}
        >
          {renderContent()}
        </SectionErrorBoundary>
        <StaleWarning />
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      number={number}
      title="実装するカスタマイズ（カスタマイズ仕様書）"
      status={status}
      detailPath={detailPath}
      onGenerate={() => onGenerate("customize-specification")}
      disableGenerate={!canRegenerate}
    >
      <SectionErrorBoundary
        sectionName="実装するカスタマイズ（カスタマイズ仕様書）"
        detailPath={detailPath}
      >
        {renderContent()}
      </SectionErrorBoundary>
    </SectionWrapper>
  );
};
